/**
 * Authentication Context
 *
 * Provides authentication state and functions throughout the application
 */

'use client';

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import type { User } from '@/lib/session';
import { logger } from '@/lib/logger';
import { getFingerprint } from '@/lib/get-fingerprint';
import { getUseChatLimit } from '@/lib/get-use-chat-limit';
import { toast } from 'sonner';
import { normalizeMtAccounts } from '@/lib/mt-accounts';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  authenticated: boolean;
  setUser: (user: User | null) => void;
  isLogin: boolean;
  setIsLogin: (isLogin: boolean) => void;
  remaining: number;
  setRemaining: React.Dispatch<React.SetStateAction<number>>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/**
 * Auth Provider Component
 *
 * Wraps the application and provides authentication state
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLogin, setIsLogin] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  // 用户剩余使用次数
  const [remaining, setRemaining] = useState<number>(3);

  /**
   * Update remaining times when user changes
   * @param user - The updated user object
   */
  useEffect(() => {
    if (!user) {
      setRemaining(3);
      return;
    }

    const dailyLimit = getUseChatLimit(user.level, user.remaining_days, user.limit);
    const reportedRemaining = typeof user.remaining === 'number' ? user.remaining : dailyLimit;
    setRemaining(Math.min(Math.max(0, reportedRemaining), dailyLimit));
  }, [user]);

  const fetchVisitor = useCallback(async (fingerId: string) => {
    try {
      const response = await fetch(`/api/auth/visitor?userId=${fingerId}`);
      if (response.ok) {
        const data = await response.json();
        logger.info('[Auth] Visitor data:', data);
        if (!data.success) {
          setUser(null);
          setIsLogin(false);
          return;
        }
        setUser({
          ...data.user,
          userId: fingerId,
        });
        setIsLogin(false);
        logger.info('[Auth] Visitor data fetched:', data);
      } else {
        setUser(null);
        setIsLogin(false);
        logger.error('[Auth] Error fetching visitor data:', response.status);
      }
    } catch (error) {
      setUser(null);
      setIsLogin(false);
      logger.error('[Auth] Error fetching visitor data:', error);
    }
  }, []);

  /**
   * Get fingerprint id
   */
  const getFingerprintId = useCallback(async () => {
    try {
      const result = await getFingerprint();
      await fetchVisitor(result.fingerprint);
    } catch (error) {
      setUser(null);
      setIsLogin(false);
      logger.error('[Auth] Error fetching fingerprint id:', error);
    }
  }, [fetchVisitor]);

  /**
   * Fetch current user from API
   */
  const fetchUser = useCallback(async () => {
    try {
      const response = await fetch('/api/auth/user');

      if (response.ok) {
        const data = await response.json();

        if (data.isTourist) {
          await getFingerprintId();
          return;
        }

        if (!data.user) {
          return;
        }
        setUser({
          ...data.user,
          userId: data.user.id,
          mt_account: normalizeMtAccounts(data.user.mt_account ?? data.mt_account),
        });
        setIsLogin(true);
        logger.info('[Auth] User authenticated:', data.user.email);
      } else {
        setUser(null);
        setIsLogin(false);
        if (response.status === 401) {
          toast.error('Session expired. Switched to guest mode.', { position: 'top-center' });
          await getFingerprintId();
          return;
        }
        logger.info('[Auth] User not authenticated');
      }
    } catch (error) {
      logger.error('[Auth] Error fetching user:', error);
      setUser(null);
      setIsLogin(false);
    } finally {
      setLoading(false);
    }
  }, [getFingerprintId]);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  const value: AuthContextType = {
    user,
    loading,
    authenticated: user !== null,
    setUser,
    isLogin,
    setIsLogin,
    remaining,
    setRemaining,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * useAuth Hook
 *
 * Access authentication state and functions from any component
 *
 * @example
 * const { user, authenticated, login, logout } = useAuth();
 */
export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);

  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }

  return context;
}
